import { CanceledError } from "axios";
import type { ActionContext, ActionHandler, Module, Store } from "vuex";

import { useAuth } from "@/utils/authentication";

export const guardTenantModule = <S, R>(module: Module<S, R>): Module<S, R> => {
  const actions = Object.fromEntries(
    Object.entries(module.actions ?? {}).map(([name, action]) => {
      const handler = typeof action === "function" ? action : action.handler;
      const guarded: ActionHandler<S, R> = function (context, payload) {
        const generation = useAuth().getGeneration();
        const assertCurrent = () => {
          if (generation !== useAuth().getGeneration())
            throw new CanceledError("Session changed");
        };
        const guard = <F extends (...args: any[]) => any>(operation: F): F =>
          ((...args: Parameters<F>) => {
            assertCurrent();
            return operation(...args);
          }) as F;
        const guardedContext: ActionContext<S, R> = {
          ...context,
          commit: guard(context.commit),
          dispatch: guard(context.dispatch),
        };
        const guardedStore = new Proxy(this, {
          get: (target, property) => {
            if (property === "commit" || property === "dispatch")
              return guard(target[property]);
            return Reflect.get(target, property);
          },
        }) as Store<R>;
        return Promise.resolve(
          handler.call(guardedStore, guardedContext, payload),
        ).then(
          (result) => {
            assertCurrent();
            return result;
          },
          (error) => {
            assertCurrent();
            throw error;
          },
        );
      };
      return [
        name,
        typeof action === "function"
          ? guarded
          : { ...action, handler: guarded },
      ];
    }),
  );
  return { ...module, actions };
};
